import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import type { User } from '@/types';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (token: string) => void;
  loginWithWiser: (accountsSessionToken: string) => Promise<void>;
  logout: () => void;
}

const API_BASE = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api`
  : '/api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() =>
    localStorage.getItem('wiserweb_ai_token')
  );
  const [isLoading, setIsLoading] = useState(true);

  // Decode JWT to get user info
  const decodeToken = useCallback((token: string): User | null => {
    try {
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload));
      return {
        id: decoded.sub,
        email: decoded.email,
        name: decoded.name,
        tier: decoded.tier,
      };
    } catch {
      return null;
    }
  }, []);

  // Check if token is expired
  const isTokenExpired = useCallback((token: string): boolean => {
    try {
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload));
      return decoded.exp * 1000 < Date.now();
    } catch {
      return true;
    }
  }, []);

  const login = useCallback((newToken: string) => {
    localStorage.setItem('wiserweb_ai_token', newToken);
    api.setToken(newToken);
    setToken(newToken);
    setUser(decodeToken(newToken));
  }, [decodeToken]);

  // WiserWeb single-account login: the shared @wiser/auth form does the content-blind wiser-accounts
  // sign-in, then we trade its opaque session token for a native WiserWeb AI JWT at POST /api/auth/wiser
  // (the backend provisions/looks up the local user). We forward ONLY the session token — never key
  // material. From there it reuses the exact same login() plumbing (storage key/format unchanged), so a
  // wiser-authed session is indistinguishable from a bespoke one and existing sessions are preserved.
  const loginWithWiser = useCallback(async (accountsSessionToken: string) => {
    const response = await fetch(`${API_BASE}/auth/wiser`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accounts_session_token: accountsSessionToken }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'WiserWeb sign-in failed');
    }
    const data = await response.json();
    login(data.token);
  }, [login]);

  const logout = useCallback(() => {
    localStorage.removeItem('wiserweb_ai_token');
    api.setToken(null);
    setToken(null);
    setUser(null);
    // Navigate will be handled by ProtectedRoute
  }, []);

  useEffect(() => {
    // Check for token in URL (from WiserWebCloud redirect - legacy support)
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');

    if (urlToken) {
      // Remove token from URL
      window.history.replaceState({}, '', window.location.pathname);
      login(urlToken);
      setIsLoading(false);
      return;
    }

    // Validate existing token
    if (token) {
      if (isTokenExpired(token)) {
        logout();
      } else {
        api.setToken(token);
        setUser(decodeToken(token));
      }
    }

    setIsLoading(false);
  }, [token, login, logout, decodeToken, isTokenExpired]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isLoading,
        isAuthenticated: !!user,
        login,
        loginWithWiser,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
